<template>
  <div class="setup-wizard-container">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h4 class="mb-1">{{ $t('setupWizard.title') }}</h4>
            <p class="text-muted mb-0">{{ $t('setupWizard.subtitle') }}</p>
          </div>
          <button 
            class="btn btn-primary"
            @click="refreshData"
            :disabled="isLoading"
          >
            <i class="ph-duotone ph-arrows-clockwise me-2"></i>
            {{ $t('common.refresh') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Nozzle Client Tenants Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <i class="ph-duotone ph-gear me-2"></i>
              {{ $t('setupWizard.nozzleClientTenants') }}
            </h5>
            <button 
              class="btn btn-success btn-sm"
              @click="createNewTenant"
              :disabled="isLoading"
            >
              <i class="ph-duotone ph-plus me-1"></i>
              {{ $t('setupWizard.createNew') }}
            </button>
          </div>
          <div class="card-body">
            <!-- Search -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control"
                    :placeholder="$t('setupWizard.searchNozzleTenants')"
                    v-model="nozzleTenantsSearch"
                    @input="debouncedSearchNozzleTenants"
                  />
                  <button class="btn btn-outline-secondary" type="button">
                    <i class="ph-duotone ph-magnifying-glass"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Nozzle Client Tenants List -->
            <div v-if="isLoadingNozzleTenants" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>

            <div v-else-if="nozzleClientTenants.length > 0" class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>{{ $t('setupWizard.tenantName') }}</th>
                    <th>{{ $t('setupWizard.apiUrl') }}</th>
                    <th>{{ $t('setupWizard.status') }}</th>
                    <th>{{ $t('setupWizard.createdDate') }}</th>
                    <th>{{ $t('common.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tenant in nozzleClientTenants" :key="tenant.id">
                    <td>{{ tenant.name }}</td>
                    <td>
                      <a 
                        v-if="tenant.settings?.nozzleClientApiUrl" 
                        :href="tenant.settings.nozzleClientApiUrl" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="text-decoration-none"
                      >
                        {{ tenant.settings.nozzleClientApiUrl }}
                        <i class="ph-duotone ph-arrow-square-out ms-1"></i>
                      </a>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <span class="badge" :class="tenant.isActive ? 'bg-success' : 'bg-secondary'">
                        {{ tenant.isActive ? $t('common.active') : $t('common.inactive') }}
                      </span>
                    </td>
                    <td>{{ formatDate(tenant.creationTime) }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-info me-2"
                        @click="viewNozzleTenantSetup(tenant.id)"
                        :disabled="isLoading"
                      >
                        <i class="ph-duotone ph-eye me-1"></i>
                        {{ $t('common.view') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-4">
              <i class="ph-duotone ph-gear text-muted" style="font-size: 3rem;"></i>
              <p class="text-muted mt-2">{{ $t('setupWizard.noNozzleTenants') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div class="modal fade" id="detailsModal" tabindex="-1" ref="detailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="ph-duotone ph-info me-2"></i>
              {{ $t('setupWizard.tenantDetails') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingDetails" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            <div v-else-if="selectedTenantDetails">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <strong>{{ $t('setupWizard.tenantName') }}:</strong>
                  <p>{{ selectedTenantDetails.name }}</p>
                </div>
                <div class="col-md-6 mb-3">
                  <strong>{{ $t('setupWizard.status') }}:</strong>
                  <p>
                    <span class="badge" :class="selectedTenantDetails.isActive ? 'bg-success' : 'bg-secondary'">
                      {{ selectedTenantDetails.isActive ? $t('common.active') : $t('common.inactive') }}
                    </span>
                  </p>
                </div>
                <div class="col-md-6 mb-3">
                  <strong>{{ $t('setupWizard.createdDate') }}:</strong>
                  <p>{{ formatDate(selectedTenantDetails.creationTime) }}</p>
                </div>
              </div>
              
               <!-- Nozzle Info Section -->
               <div class="mt-4">
                 <div class="accordion" id="nozzleInfoAccordion">
                   <div class="accordion-item">
                     <h2 class="accordion-header">
                       <button 
                         class="accordion-button collapsed" 
                         type="button" 
                         data-bs-toggle="collapse" 
                         data-bs-target="#nozzleInfoCollapse" 
                         aria-expanded="false" 
                         aria-controls="nozzleInfoCollapse"
                       >
                         <i class="ph-duotone ph-gear me-2 text-primary"></i>
                         <strong class="text-primary">Nozzle Info</strong>
                       </button>
                     </h2>
                     <div 
                       id="nozzleInfoCollapse" 
                       class="accordion-collapse collapse" 
                       data-bs-parent="#nozzleInfoAccordion"
                     >
                       <div class="accordion-body">
                         <div class="row">
                           <div class="col-12 mb-3">
                             <strong>{{ $t('setupWizard.nozzleClientApiUrl') }}:</strong>
                             <p class="text-break">
                               <a 
                                 v-if="selectedTenantDetails.settings?.nozzleClientApiUrl" 
                                 :href="selectedTenantDetails.settings.nozzleClientApiUrl" 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 class="text-decoration-none"
                               >
                                 {{ selectedTenantDetails.settings.nozzleClientApiUrl }}
                                 <i class="ph-duotone ph-arrow-square-out ms-1"></i>
                               </a>
                               <span v-else>-</span>
                             </p>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>{{ $t('setupWizard.nozzleClientUsername') }}:</strong>
                             <p>{{ selectedTenantDetails.settings?.nozzleClientUsername || '-' }}</p>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>{{ $t('setupWizard.nozzleClientPassword') }}:</strong>
                             <p>{{ selectedTenantDetails.settings?.nozzleClientPassword || '-' }}</p>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
              
               <!-- Supplier Info Section -->
               <div class="mt-4">
                 <div class="accordion" id="supplierInfoAccordion">
                   <div class="accordion-item">
                     <h2 class="accordion-header">
                       <button 
                         class="accordion-button collapsed" 
                         type="button" 
                         data-bs-toggle="collapse" 
                         data-bs-target="#supplierInfoCollapse" 
                         aria-expanded="false" 
                         aria-controls="supplierInfoCollapse"
                       >
                         <i class="ph-duotone ph-users me-2 text-success"></i>
                         <strong class="text-success">Supplier Info</strong>
                       </button>
                     </h2>
                     <div 
                       id="supplierInfoCollapse" 
                       class="accordion-collapse collapse" 
                       data-bs-parent="#supplierInfoAccordion"
                     >
                       <div class="accordion-body">
                         <div class="row">
                           <div class="col-12 mb-3">
                             <strong>Supplier URL:</strong>
                             <p>{{ apiBaseUrl }}</p>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>Tenant ID:</strong>
                             <p>{{ selectedTenantDetails.id || '-' }}</p>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>Username:</strong>
                             <p>{{ selectedTenantDetails.users?.[0]?.userName || '-' }}</p>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>Password:</strong>
                             <p v-if="selectedTenantDetails.users?.[0]?.plainPassword">
                               {{ selectedTenantDetails.users[0].plainPassword }}
                             </p>
                             <div v-else class="alert alert-warning p-2 mb-0">
                               <i class="ph-duotone ph-warning me-1"></i>
                               {{ $t('setupWizard.missingPasswordWarning') }}
                             </div>
                           </div>
                           <div class="col-md-6 mb-3">
                             <strong>Environment:</strong>
                             <p>Production</p>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
              
              <!-- JSON Configuration Section -->
              <div class="mt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0">
                    <i class="ph-duotone ph-code me-2"></i>
                    {{ $t('setupWizard.jsonConfiguration') }}
                  </h6>
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-primary"
                    @click="copyJsonConfiguration"
                    :disabled="isLoading"
                  >
                    <i class="ph-duotone ph-copy me-1"></i>
                    {{ $t('setupWizard.copyJson') }}
                  </button>
                </div>
                <div class="json-container bg-dark rounded p-3">
                  <pre class="json-content mb-0" v-html="formatJsonConfigurationWithHighlight"></pre>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-success me-2"
              @click="testLogin"
              :disabled="isLoading || isTestingLogin"
            >
              <span v-if="isTestingLogin" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ph-duotone ph-play me-1"></i>
              {{ $t('setupWizard.testLogin') }}
            </button>
            <button type="button" class="btn btn-info me-2" @click="openTenantSettings" :disabled="!selectedTenantDetails">
              <i class="ph-duotone ph-gear me-1"></i>
              {{ $t('setupWizard.tenantSettings') }}
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create New Tenant Modal -->
    <div class="modal fade" id="createTenantModal" tabindex="-1" ref="createTenantModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="ph-duotone ph-plus me-2"></i>
              {{ $t('setupWizard.createNewTenant') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Create Tenant -->
            <div v-if="createStep === 1">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-one me-2"></i>
                {{ $t('setupWizard.step1Title') }}
              </h6>
              <form @submit.prevent="createTenant">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.tenantName') }} *</label>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control"
                        v-model="createForm.name"
                        required
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="autoFillForm"
                        :disabled="!createForm.name.trim()"
                        :title="$t('setupWizard.autoFillTooltip')"
                      >
                        <i class="ph-duotone ph-magic-wand"></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">{{ $t('setupWizard.adminEmail') }} *</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="createForm.adminEmail"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.adminPassword') }} *</label>
                    <input
                      type="password"
                      class="form-control"
                      v-model="createForm.adminPassword"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.adminName') }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="createForm.adminName"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.adminSurname') }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="createForm.adminSurname"
                    />
                  </div>
                </div>
              </form>
            </div>

            <!-- Step 2: Credential Validation -->
            <div v-else-if="createStep === 2">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-two me-2"></i>
                {{ $t('setupWizard.step2Title') }}
              </h6>
              <div class="alert alert-success">
                <i class="ph-duotone ph-check-circle me-2"></i>
                {{ $t('setupWizard.tenantCreatedSuccess') }}
              </div>
              <div class="mb-3">
                <p>{{ $t('setupWizard.validatingCredentials') }}</p>
                <div v-if="isValidatingCredentials" class="text-center py-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ $t('common.loading') }}</span>
                  </div>
                </div>
                <div v-else-if="credentialValidationResult">
                  <div class="alert" :class="credentialValidationResult.success ? 'alert-success' : 'alert-danger'">
                    <i class="ph-duotone" :class="credentialValidationResult.success ? 'ph-check-circle' : 'ph-x-circle'" me-2></i>
                    {{ credentialValidationResult.message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Nozzle URL Check -->
            <div v-else-if="createStep === 3">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-three me-2"></i>
                {{ $t('setupWizard.step3Title') }}
              </h6>
              <div class="mb-3">
                <label class="form-label">{{ $t('setupWizard.nozzleUrl') }} *</label>
                <div class="input-group">
                  <input
                    type="url"
                    class="form-control"
                    v-model="nozzleUrl"
                    :class="nozzleUrlStatus === 'success' ? 'is-valid' : nozzleUrlStatus === 'error' ? 'is-invalid' : ''"
                    required
                  />
                  <span v-if="nozzleUrlStatus === 'success'" class="input-group-text text-success">
                    <i class="ph-duotone ph-check-circle"></i>
                  </span>
                  <span v-else-if="nozzleUrlStatus === 'error'" class="input-group-text text-danger">
                    <i class="ph-duotone ph-x-circle"></i>
                  </span>
                  <button
                    v-if="nozzleUrlStatus === 'error' || nozzleUrlStatus === 'pending'"
                    type="button"
                    class="btn btn-outline-primary"
                    @click="checkNozzleUrl"
                    :disabled="isCheckingUrl || !nozzleUrl.trim()"
                  >
                    <span v-if="isCheckingUrl" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="ph-duotone ph-check me-1"></i>
                    {{ $t('setupWizard.checkUrl') }}
                  </button>
                </div>
                <div v-if="nozzleUrlStatus === 'error'" class="invalid-feedback d-block">
                  {{ $t('setupWizard.urlCheckFailed') }}
                </div>
              </div>
            </div>

            <!-- Step 4: Support Account Creation -->
            <div v-else-if="createStep === 4">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-four me-2"></i>
                {{ $t('setupWizard.step4Title') }}
              </h6>
              <div class="alert alert-info">
                <i class="ph-duotone ph-question me-2"></i>
                {{ $t('setupWizard.supportAccountQuestion') }}
              </div>
              
              <div class="mb-4">
                <h6>{{ $t('setupWizard.mainSupportAccountInfo') }}</h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.supportUsername') }} *</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="supportCredentials.username"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.supportPassword') }} *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="supportCredentials.password"
                      required
                    />
                  </div>
                </div>
              </div>

              <div v-if="isCreatingSupportAccount" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
                <p class="mt-2">{{ $t('setupWizard.creatingSupportAccount') }}</p>
              </div>

              <div v-else-if="supportAccountResult">
                <div class="alert" :class="supportAccountResult.success ? 'alert-success' : 'alert-danger'">
                  <i class="ph-duotone" :class="supportAccountResult.success ? 'ph-check-circle' : 'ph-x-circle'" me-2></i>
                  {{ supportAccountResult.message }}
                </div>
              </div>
            </div>

            <!-- Step 5: Create User Account -->
            <div v-else-if="createStep === 5">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-five me-2"></i>
                {{ $t('setupWizard.step5Title') }}
              </h6>
              <div class="alert alert-info">
                <i class="ph-duotone ph-user-plus me-2"></i>
                {{ $t('setupWizard.createUserAccountQuestion') }}
              </div>
              
              <div class="mb-4">
                <h6>{{ $t('setupWizard.userAccountInfo') }}</h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.userName') }} *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="userAccountData.name"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.userSurname') }} *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="userAccountData.surname"
                      required
                    />
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">{{ $t('setupWizard.userEmail') }} *</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="userAccountData.email"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.userPassword') }} *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="userAccountData.password"
                      required
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.userRetypePassword') }} *</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="userAccountData.retypePassword"
                      required
                    />
                  </div>
                </div>
              </div>

              <div v-if="isCreatingUserAccount" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
                <p class="mt-2">{{ $t('setupWizard.creatingUserAccount') }}</p>
              </div>

              <div v-else-if="userAccountResult">
                <div class="alert" :class="userAccountResult.success ? 'alert-success' : 'alert-danger'">
                  <i class="ph-duotone" :class="userAccountResult.success ? 'ph-check-circle' : 'ph-x-circle'" me-2></i>
                  {{ userAccountResult.message }}
                </div>
              </div>
            </div>

            <!-- Step 6: Create Setup -->
            <div v-else-if="createStep === 6">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-six me-2"></i>
                {{ $t('setupWizard.step6Title') }}
              </h6>
              <div class="alert alert-info">
                <i class="ph-duotone ph-gear me-2"></i>
                {{ $t('setupWizard.createSetupInfo') }}
              </div>
              
              <div v-if="isCreatingSetup" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
                <p class="mt-2">{{ $t('setupWizard.creatingSetup') }}</p>
              </div>

              <div v-else-if="setupResult">
                <div class="alert" :class="setupResult.success ? 'alert-success' : 'alert-danger'">
                  <i class="ph-duotone" :class="setupResult.success ? 'ph-check-circle' : 'ph-x-circle'" me-2></i>
                  {{ setupResult.message }}
                </div>
              </div>
            </div>

            <!-- Step 7: Test Login -->
            <div v-else-if="createStep === 7">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-seven me-2"></i>
                {{ $t('setupWizard.step7Title') }}
              </h6>
              <div class="alert alert-info">
                <i class="ph-duotone ph-flask me-2"></i>
                {{ $t('setupWizard.testLoginInfo') }}
              </div>
              
              <div v-if="isTestingUserLogin" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">{{ $t('common.loading') }}</span>
                </div>
                <p class="mt-2">{{ $t('setupWizard.testingUserLogin') }}</p>
              </div>

              <div v-else-if="userLoginTestResult">
                <div class="alert" :class="userLoginTestResult.success ? 'alert-success' : 'alert-danger'">
                  <i class="ph-duotone" :class="userLoginTestResult.success ? 'ph-check-circle' : 'ph-x-circle'" me-2></i>
                  {{ userLoginTestResult.message }}
                </div>
              </div>
            </div>

            <!-- Step 8: Setup Complete -->
            <div v-else-if="createStep === 8">
              <h6 class="mb-3">
                <i class="ph-duotone ph-number-circle-eight me-2"></i>
                {{ $t('setupWizard.step8Title') }}
              </h6>
              <div class="alert alert-success">
                <i class="ph-duotone ph-check-circle me-2"></i>
                {{ $t('setupWizard.setupComplete') }}
              </div>
              <p>{{ $t('setupWizard.nextStepsMessage') }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              v-if="createStep === 1"
              type="button" 
              class="btn btn-primary"
              @click="createTenant"
              :disabled="isCreatingTenant"
            >
              <span v-if="isCreatingTenant" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ph-duotone ph-plus me-1"></i>
              {{ $t('setupWizard.createTenant') }}
            </button>
            <button 
              v-else-if="createStep === 2"
              type="button" 
              class="btn"
              :class="credentialValidationResult && !credentialValidationResult.success ? 'btn-warning' : 'btn-primary'"
              @click="validateNewCredentials"
              :disabled="isValidatingCredentials"
            >
              <span v-if="isValidatingCredentials" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ph-duotone" :class="credentialValidationResult && !credentialValidationResult.success ? 'ph-arrow-clockwise' : 'ph-check'"></i>
              {{ credentialValidationResult && !credentialValidationResult.success ? $t('setupWizard.retryValidation') : $t('setupWizard.validateCredentials') }}
            </button>
            <button 
              v-else-if="createStep === 3"
              type="button" 
              class="btn btn-primary"
              @click="proceedToSupportAccount"
              :disabled="nozzleUrlStatus !== 'success'"
            >
              <i class="ph-duotone ph-arrow-right me-1"></i>
              {{ $t('setupWizard.proceedToSupport') }}
            </button>
            <button 
              v-else-if="createStep === 4"
              type="button" 
              class="btn btn-primary"
              @click="createSupportAccount"
              :disabled="isCreatingSupportAccount || !supportCredentials.username || !supportCredentials.password"
            >
              <span v-if="isCreatingSupportAccount" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ph-duotone ph-user-plus me-1"></i>
              {{ $t('setupWizard.loginSupportAccount') }}
            </button>
            <button 
              v-else-if="createStep === 5"
              type="button" 
              class="btn btn-primary"
              @click="createUserAccount"
              :disabled="isCreatingUserAccount || !userAccountData.name || !userAccountData.surname || !userAccountData.email || !userAccountData.password || !userAccountData.retypePassword"
            >
              <span v-if="isCreatingUserAccount" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ph-duotone ph-user-plus me-1"></i>
              {{ $t('setupWizard.createUserAccount') }}
            </button>
            <button 
              v-else-if="createStep === 8"
              type="button" 
              class="btn btn-success"
              @click="finishCreation"
            >
              <i class="ph-duotone ph-check me-1"></i>
              {{ $t('setupWizard.finish') }}
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tenant Settings Modal -->
    <div class="modal fade" id="tenantSettingsModal" tabindex="-1" aria-labelledby="tenantSettingsModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="tenantSettingsModalLabel">
              <i class="ph-duotone ph-gear me-2"></i>
              {{ $t('setupWizard.tenantSettings') }}
            </h5>
            <button type="button" class="btn-close" @click="closeTenantSettingsModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingTenantSettings" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            <div v-else-if="tenantSettings">
              <form @submit.prevent="saveTenantSettings">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.tenantName') }}</label>
                    <input type="text" class="form-control" v-model="tenantSettingsForm.tenantName" readonly />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.tenantId') }}</label>
                    <input type="text" class="form-control" v-model="tenantSettingsForm.tenantId" readonly />
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">{{ $t('setupWizard.nozzleClientApiUrl') }}</label>
                    <input type="url" class="form-control" v-model="tenantSettingsForm.nozzleClientApiUrl" :disabled="!isEditingTenantSettings" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.nozzleClientUsername') }}</label>
                    <input type="text" class="form-control" v-model="tenantSettingsForm.nozzleClientUsername" :disabled="!isEditingTenantSettings" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.nozzleClientPassword') }}</label>
                    <input type="text" class="form-control" v-model="tenantSettingsForm.nozzleClientPassword" :disabled="!isEditingTenantSettings" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.nozzleClientInfoMail') }}</label>
                    <input type="email" class="form-control" v-model="tenantSettingsForm.nozzleClientInfoMail" :disabled="!isEditingTenantSettings" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ $t('setupWizard.nozzleClientSenderMail') }}</label>
                    <input type="email" class="form-control" v-model="tenantSettingsForm.nozzleClientSenderMail" :disabled="!isEditingTenantSettings" />
                  </div>
                  <div class="col-12 mb-3">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" v-model="tenantSettingsForm.requireConfirmedEmail" :disabled="!isEditingTenantSettings" id="requireConfirmedEmail">
                      <label class="form-check-label" for="requireConfirmedEmail">
                        {{ $t('setupWizard.requireConfirmedEmail') }}
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="modal-footer">
            <button v-if="!isEditingTenantSettings" type="button" class="btn btn-primary" @click="startEditingTenantSettings">
              <i class="ph-duotone ph-pencil me-1"></i>
              {{ $t('common.edit') }}
            </button>
            <template v-else>
              <button type="button" class="btn btn-success me-2" @click="saveTenantSettings" :disabled="isSavingTenantSettings">
                <span v-if="isSavingTenantSettings" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="ph-duotone ph-check me-1"></i>
                {{ $t('common.save') }}
              </button>
              <button type="button" class="btn btn-outline-secondary me-2" @click="cancelEditingTenantSettings">
                <i class="ph-duotone ph-x me-1"></i>
                {{ $t('common.cancel') }}
              </button>
            </template>
            <button type="button" class="btn btn-secondary" @click="closeTenantSettingsModal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import superAdminSetupService from '@/services/superAdminSetupService';
import { useDateFormatter } from '@/composables/useDateFormatter';

const { t } = useI18n();
const store = useStore();
const { formatDate } = useDateFormatter();

// Reactive data
const isLoading = ref(false);
const isLoadingNozzleTenants = ref(false);
const isLoadingDetails = ref(false);
const isTestingLogin = ref(false);
const error = ref(null);

// Create tenant wizard data
const createStep = ref(1);
const isCreatingTenant = ref(false);
const isValidatingCredentials = ref(false);
const credentialValidationResult = ref(null);
const createForm = ref({
  name: '',
  adminEmail: '',
  adminPassword: '',
  adminName: '',
  adminSurname: ''
});
const createdTenantData = ref(null);

// Step 3 & 4 data
const nozzleUrl = ref('');
const nozzleUrlStatus = ref('pending'); // pending, success, error
const isCheckingUrl = ref(false);
const supportCredentials = ref({
  username: 'support@nozzlesoft.com',
  password: 'nzzl2024temmuz&!@!'
});
const isCreatingSupportAccount = ref(false);
const supportAccountResult = ref(null);
const supportToken = ref(null);

// Step 5 & 6 data
const userAccountData = ref({
  name: 'supplier@nozzlesoft.com',
  surname: 'supplier@nozzlesoft.com',
  email: 'supplier@nozzlesoft.com',
  password: '',
  retypePassword: ''
});
const isCreatingUserAccount = ref(false);
const userAccountResult = ref(null);
const isTestingUserLogin = ref(false);
const userLoginTestResult = ref(null);

// Step 6 & 7 data
const isCreatingSetup = ref(false);
const setupResult = ref(null);
const isCreatingSettings = ref(false);
const settingsResult = ref(null);

// Auto-fill form function
const autoFillForm = () => {
  if (!createForm.value.name.trim()) return;
  
  // Generate admin email: {tenantName}_supplier@nozzlesoft.com
  const tenantName = createForm.value.name.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove special characters
    .replace(/\s+/g, ''); // Remove spaces
  
  createForm.value.adminEmail = `${tenantName}_supplier@nozzlesoft.com`;
  
  // Generate random password (visible to user)
  createForm.value.adminPassword = generatePassword();
  
  // Set name and surname
  createForm.value.adminName = 'Supplier';
  createForm.value.adminSurname = 'User';
  
  // Show notification
  store.dispatch('notify', {
    message: t('setupWizard.autoFillSuccess'),
    type: 'success'
  });
};

// Generate random password function
const generatePassword = () => {
  const length = 12;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!';
  let password = '';
  
  // Ensure at least one character from each category
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!';
  
  password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
  password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
  password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  password += symbols.charAt(Math.floor(Math.random() * symbols.length));
  
  // Fill the rest randomly
  for (let i = 4; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

// Computed properties
const apiBaseUrl = computed(() => import.meta.env.VITE_APP_API_URL || 'https://api.vesproc.com');

const nozzleClientTenants = ref([]);
const selectedTenantDetails = ref(null);

const nozzleTenantsSearch = ref('');

const detailsModal = ref(null);
const createTenantModal = ref(null);

// Tenant settings data
const tenantSettings = ref(null);
const tenantSettingsForm = ref({});
const isLoadingTenantSettings = ref(false);
const isEditingTenantSettings = ref(false);
const isSavingTenantSettings = ref(false);
const originalTenantSettings = ref(null);

// Search debounce timeouts
let nozzleTenantsTimeout = null;

// Watch for URL changes to reset status
watch(nozzleUrl, () => {
  resetUrlStatus();
});

onMounted(() => {
  loadNozzleClientTenants();
});


const loadNozzleClientTenants = async () => {
  isLoadingNozzleTenants.value = true;
  error.value = null;

  try {
    const response = await superAdminSetupService.getNozzleClientTenants(nozzleTenantsSearch.value);
    nozzleClientTenants.value = response.items || [];
  } catch (err) {
    console.error('Load nozzle client tenants error:', err);
    error.value = err.response?.data?.error?.message || t('setupWizard.loadError');
    store.dispatch('notify', {
      message: t('setupWizard.loadError'),
      type: 'error'
    });
  } finally {
    isLoadingNozzleTenants.value = false;
  }
};

const debouncedSearchNozzleTenants = () => {
  if (nozzleTenantsTimeout) {
    clearTimeout(nozzleTenantsTimeout);
  }
  nozzleTenantsTimeout = setTimeout(() => {
    loadNozzleClientTenants();
  }, 500);
};


const viewNozzleTenantSetup = async (tenantId) => {
  isLoadingDetails.value = true;
  selectedTenantDetails.value = null;

  try {
    const response = await superAdminSetupService.getNozzleClientTenantSetup(tenantId);
    selectedTenantDetails.value = response;
    console.log('Selected tenant details:', response);
    
    // Show modal
    const modal = new bootstrap.Modal(detailsModal.value);
    modal.show();
  } catch (err) {
    console.error('Load nozzle tenant setup error:', err);
    error.value = err.response?.data?.error?.message || t('setupWizard.loadDetailsError');
    store.dispatch('notify', {
      message: t('setupWizard.loadDetailsError'),
      type: 'error'
    });
  } finally {
    isLoadingDetails.value = false;
  }
};

const refreshData = async () => {
  isLoading.value = true;
  await loadNozzleClientTenants();
  isLoading.value = false;
  
  store.dispatch('notify', {
    message: t('common.dataRefreshed'),
    type: 'success'
  });
};

const createNewTenant = () => {
  // Reset wizard state
  createStep.value = 1;
  credentialValidationResult.value = null;
  createdTenantData.value = null;
  createForm.value = {
    name: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    adminSurname: ''
  };
  
  // Reset additional data
  nozzleUrl.value = '';
  nozzleUrlStatus.value = 'pending';
  supportAccountResult.value = null;
  supportToken.value = null;
  userAccountResult.value = null;
  userLoginTestResult.value = null;
  setupResult.value = null;
  settingsResult.value = null;
  
  // Auto-generate password for user account
  userAccountData.value.password = generatePassword();
  userAccountData.value.retypePassword = userAccountData.value.password;
  
  // Show modal
  const modal = new bootstrap.Modal(createTenantModal.value);
  modal.show();
};

// Step 1: Create tenant
const createTenant = async () => {
  if (!createForm.value.name || !createForm.value.adminEmail || !createForm.value.adminPassword) {
    store.dispatch('notify', {
      message: t('setupWizard.requiredFieldsError'),
      type: 'error'
    });
    return;
  }

  isCreatingTenant.value = true;
  error.value = null;

  try {
    const tenantData = {
      name: createForm.value.name,
      adminEmail: createForm.value.adminEmail,
      adminPassword: createForm.value.adminPassword,
      adminName: createForm.value.adminName || null,
      adminSurname: createForm.value.adminSurname || null
    };

    const response = await superAdminSetupService.createNozzleClientTenant(tenantData);
    createdTenantData.value = response;
    
    console.log('Created tenant response:', response);
    
    // Move to step 2
    createStep.value = 2;
    
    // Auto-validate credentials
    setTimeout(() => {
      validateNewCredentials();
    }, 1000);
    
  } catch (err) {
    console.error('Create tenant error:', err);
    const errorMessage = err.response?.data?.error?.message || 
                        err.response?.data?.message || 
                        t('setupWizard.createTenantError');
    
    error.value = errorMessage;
    store.dispatch('notify', {
      message: errorMessage,
      type: 'error'
    });
  } finally {
    isCreatingTenant.value = false;
  }
};

// Step 2: Validate credentials
const validateNewCredentials = async () => {
  isValidatingCredentials.value = true;
  credentialValidationResult.value = null;

  try {
    const credentialsData = {
      username: createForm.value.adminEmail,
      password: createForm.value.adminPassword
    };

    const response = await superAdminSetupService.validateCredentials(credentialsData);
    
    // Check if validation was successful based on the response
    if (response && response.isValid) {
      credentialValidationResult.value = {
        success: true,
        message: t('setupWizard.credentialValidationSuccess')
      };
      
    // Move to step 3 after successful validation and auto-fill nozzle URL
    setTimeout(() => {
      createStep.value = 3;
      // Auto-fill nozzle URL based on tenant name
      const tenantName = createForm.value.name.toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .replace(/\s+/g, '');
      nozzleUrl.value = `https://api${tenantName}.nozzlesoft.com`;
      nozzleUrlStatus.value = 'pending';
      
      // Auto-execute URL check
      setTimeout(() => {
        checkNozzleUrl();
      }, 1000);
    }, 2000);
    } else {
      // Validation failed
      const errorMessage = response?.message || t('setupWizard.credentialValidationError');
      credentialValidationResult.value = {
        success: false,
        message: errorMessage
      };
      
      // Don't proceed to next step
    }
    
  } catch (err) {
    console.error('Credential validation error:', err);
    const errorMessage = err.response?.data?.error?.message || 
                        err.response?.data?.message || 
                        t('setupWizard.credentialValidationError');
    
    credentialValidationResult.value = {
      success: false,
      message: errorMessage
    };
  } finally {
    isValidatingCredentials.value = false;
  }
};

// Step 3: Check Nozzle URL
const checkNozzleUrl = async () => {
  if (!nozzleUrl.value.trim()) return;
  
  isCheckingUrl.value = true;
  nozzleUrlStatus.value = 'pending';
  
  try {
    // Simple ping check - try to fetch the URL
    const response = await fetch(nozzleUrl.value, { 
      method: 'HEAD',
      mode: 'no-cors' // This will always succeed for CORS, but we can check if URL is accessible
    });
    
    // If we get here without error, URL is accessible
    nozzleUrlStatus.value = 'success';
    
    store.dispatch('notify', {
      message: t('setupWizard.urlCheckSuccess'),
      type: 'success'
    });
    
  } catch (error) {
    console.error('URL check error:', error);
    nozzleUrlStatus.value = 'error';
    
    store.dispatch('notify', {
      message: t('setupWizard.urlCheckFailed'),
      type: 'error'
    });
  } finally {
    isCheckingUrl.value = false;
  }
};

// Watch for URL changes to reset status
const resetUrlStatus = () => {
  if (nozzleUrlStatus.value !== 'pending') {
    nozzleUrlStatus.value = 'pending';
  }
};

// Step 4: Proceed to support account creation
const proceedToSupportAccount = () => {
  if (nozzleUrlStatus.value !== 'success') return;
  
  // Load saved credentials from localStorage
  loadSupportCredentials();
  
  createStep.value = 4;
};

// Load support credentials from localStorage
const loadSupportCredentials = () => {
  try {
    const saved = localStorage.getItem('supportCredentials');
    if (saved) {
      const credentials = JSON.parse(saved);
      supportCredentials.value = {
        username: credentials.username || 'support@nozzlesoft.com',
        password: credentials.password || 'nzzl2024temmuz!@!'
      };
    }
  } catch (error) {
    console.error('Error loading support credentials:', error);
  }
};

// Save support credentials to localStorage
const saveSupportCredentials = () => {
  try {
    localStorage.setItem('supportCredentials', JSON.stringify(supportCredentials.value));
  } catch (error) {
    console.error('Error saving support credentials:', error);
  }
};

// Step 4: Create support account
const createSupportAccount = async () => {
  if (!supportCredentials.value.username || !supportCredentials.value.password) return;
  
  isCreatingSupportAccount.value = true;
  supportAccountResult.value = null;
  
  try {
    // Save credentials to localStorage
    saveSupportCredentials();
    
    // Make login request to the nozzle URL
    const loginData = {
      username: supportCredentials.value.username,
      password: supportCredentials.value.password
    };
    
    const response = await fetch(`${nozzleUrl.value}/api/Account/Login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Authorization': '1'
      },
      body: JSON.stringify(loginData)
    });
    
    if (response.ok) {
      const result = await response.json();
      
      if (result.tokenBearer) {
        supportToken.value = result.tokenBearer;
        supportAccountResult.value = {
          success: true,
          message: t('setupWizard.supportAccountCreatedSuccess')
        };
        
        // Move to user account creation step
        setTimeout(() => {
          createStep.value = 5;
        }, 2000);
      } else {
        throw new Error('No token received');
      }
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
    
  } catch (error) {
    console.error('Support account creation error:', error);
    const errorMessage = error.message || t('setupWizard.supportAccountCreationError');
    
    supportAccountResult.value = {
      success: false,
      message: errorMessage
    };
  } finally {
    isCreatingSupportAccount.value = false;
  }
};

// Step 5: Create User Account
const createUserAccount = async () => {
  if (!userAccountData.value.name || !userAccountData.value.surname || 
      !userAccountData.value.email || !userAccountData.value.password || 
      !userAccountData.value.retypePassword) return;
  
  if (userAccountData.value.password !== userAccountData.value.retypePassword) {
    store.dispatch('notify', {
      message: t('setupWizard.passwordMismatch'),
      type: 'error'
    });
    return;
  }
  
  isCreatingUserAccount.value = true;
  userAccountResult.value = null;
  
  try {
    const userData = {
      isAllVessel: true,
      name: userAccountData.value.name,
      surname: userAccountData.value.surname,
      email: userAccountData.value.email,
      password: userAccountData.value.password,
      retypePassword: userAccountData.value.retypePassword,
      userGroupID: "64a375a5-c664-4a8f-a9b3-d0342d045857", // Default user group ID
      isActive: true
    };
    
    const response = await fetch(`${nozzleUrl.value}/api/General/Users/`, {
      method: 'POST',
      headers: {
        'accept': 'application/json, text/plain, */*',
        'content-type': 'application/json',
        'x-authorization': supportToken.value
      },
      body: JSON.stringify(userData)
    });
    
    if (response.ok) {
      userAccountResult.value = {
        success: true,
        message: t('setupWizard.userAccountCreatedSuccess')
      };
      
      // Move to create setup step and auto-execute
      setTimeout(() => {
        createStep.value = 6;
        // Auto-execute setup creation
        setTimeout(() => {
          createSetup();
        }, 1000);
      }, 2000);
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
    
  } catch (error) {
    console.error('User account creation error:', error);
    const errorMessage = error.message || t('setupWizard.userAccountCreationError');
    
    userAccountResult.value = {
      success: false,
      message: errorMessage
    };
  } finally {
    isCreatingUserAccount.value = false;
  }
};

// Step 6: Create Setup
const createSetup = async () => {
  isCreatingSetup.value = true;
  setupResult.value = null;
  
  try {
    // Send all the collected information to the create endpoint
    const setupData = {
      tenantId: createdTenantData.value?.id || createdTenantData.value?.tenantId,
      nozzleClientPassword: userAccountData.value.password,
      nozzleClientApiUrl: nozzleUrl.value,
      nozzleClientInfoMail: userAccountData.value.email,
      nozzleClientSenderMail: userAccountData.value.email,
      nozzleClientUsername: userAccountData.value.email,
      requireConfirmedEmail: true
    };
    
    console.log('Sending setup data:', setupData);
    
    await superAdminSetupService.createSettings(setupData);
    
    setupResult.value = {
      success: true,
      message: t('setupWizard.setupCreatedSuccess')
    };
    
      // Move to test login step and auto-execute
      setTimeout(() => {
        createStep.value = 7;
        // Auto-execute test login
        setTimeout(() => {
          testUserLogin();
        }, 1000);
      }, 2000);
    
  } catch (error) {
    console.error('Setup creation error:', error);
    const errorMessage = error.response?.data?.error?.message || 
                        error.response?.data?.message || 
                        t('setupWizard.setupCreationError');
    
    setupResult.value = {
      success: false,
      message: errorMessage
    };
  } finally {
    isCreatingSetup.value = false;
  }
};

// Step 7: Test User Login
const testUserLogin = async () => {
  isTestingUserLogin.value = true;
  userLoginTestResult.value = null;
  
  try {
    // Test with our existing test-login endpoint
    const loginData = {
      tenantId: createdTenantData.value?.id || createdTenantData.value?.tenantId
    };
    
    console.log('Sending tenantId for test login:', loginData.tenantId);
    
    const response = await superAdminSetupService.testLogin(loginData);
    
    console.log('Test login response:', response);
    
    // Check if the response indicates success
    if (response && response.isSuccess === true) {
      userLoginTestResult.value = {
        success: true,
        message: t('setupWizard.userLoginTestSuccess')
      };
      
      // Move to final step
      setTimeout(() => {
        createStep.value = 8;
      }, 2000);
    } else {
      // Login test failed
      const errorMessage = response?.message || t('setupWizard.userLoginTestError');
      userLoginTestResult.value = {
        success: false,
        message: errorMessage
      };
    }
    
  } catch (error) {
    console.error('User login test error:', error);
    const errorMessage = error.response?.data?.error?.message || 
                        error.response?.data?.message || 
                        t('setupWizard.userLoginTestError');
    
    userLoginTestResult.value = {
      success: false,
      message: errorMessage
    };
  } finally {
    isTestingUserLogin.value = false;
  }
};

// Step 8: Finish creation
const finishCreation = () => {
  // Close modal
  const modal = bootstrap.Modal.getInstance(createTenantModal.value);
  modal.hide();
  
  // Refresh tenant list
  loadNozzleClientTenants();
  
  store.dispatch('notify', {
    message: t('setupWizard.tenantCreationComplete'),
    type: 'success'
  });
};

// Tenant Settings Methods
const openTenantSettings = async () => {
  if (!selectedTenantDetails.value) return;
  
  isLoadingTenantSettings.value = true;
  try {
    const settings = await superAdminSetupService.getTenantSettings(selectedTenantDetails.value.id);
    console.log('Loaded tenant settings:', settings);
    
    tenantSettings.value = settings;
    tenantSettingsForm.value = {
      tenantId: settings.tenantId,
      tenantName: settings.tenantName,
      nozzleClientApiUrl: settings.nozzleClientApiUrl || '',
      nozzleClientUsername: settings.nozzleClientUsername || '',
      nozzleClientPassword: settings.nozzleClientPassword || '',
      nozzleClientInfoMail: settings.nozzleClientInfoMail || '',
      nozzleClientSenderMail: settings.nozzleClientSenderMail || '',
      requireConfirmedEmail: settings.requireConfirmedEmail || false
    };
    originalTenantSettings.value = { ...tenantSettingsForm.value };
    isEditingTenantSettings.value = false;
    
    // Show modal
    const modalElement = document.getElementById('tenantSettingsModal');
    const modal = new bootstrap.Modal(modalElement);
    
    // Add event listener for when modal is hidden
    modalElement.addEventListener('hidden.bs.modal', () => {
      closeTenantSettingsModal();
    }, { once: true });
    
    modal.show();
  } catch (err) {
    console.error('Error loading tenant settings:', err);
    const errorMessage = err.response?.data?.error?.message || 
                        err.response?.data?.message || 
                        t('setupWizard.tenantSettingsLoadError');
    store.dispatch('notify', {
      message: errorMessage,
      type: 'error'
    });
  } finally {
    isLoadingTenantSettings.value = false;
  }
};

const startEditingTenantSettings = () => {
  isEditingTenantSettings.value = true;
};

const cancelEditingTenantSettings = () => {
  isEditingTenantSettings.value = false;
  tenantSettingsForm.value = { ...originalTenantSettings.value };
};

const closeTenantSettingsModal = () => {
  // Clean up modal backdrop
  const modal = bootstrap.Modal.getInstance(document.getElementById('tenantSettingsModal'));
  if (modal) {
    modal.hide();
  }
  
  // Remove any remaining backdrop
  const backdrop = document.querySelector('.modal-backdrop');
  if (backdrop) {
    backdrop.remove();
  }
  
  // Restore body scroll
  document.body.classList.remove('modal-open');
  document.body.style.paddingRight = '';
};

const saveTenantSettings = async () => {
  isSavingTenantSettings.value = true;
  try {
    const updateData = {
      tenantId: tenantSettingsForm.value.tenantId,
      nozzleClientPassword: tenantSettingsForm.value.nozzleClientPassword,
      nozzleClientApiUrl: tenantSettingsForm.value.nozzleClientApiUrl,
      nozzleClientInfoMail: tenantSettingsForm.value.nozzleClientInfoMail,
      nozzleClientSenderMail: tenantSettingsForm.value.nozzleClientSenderMail,
      nozzleClientUsername: tenantSettingsForm.value.nozzleClientUsername,
      requireConfirmedEmail: tenantSettingsForm.value.requireConfirmedEmail
    };
    
    await superAdminSetupService.updateTenantSettings(updateData);
    
    // Update local data
    tenantSettings.value = { ...tenantSettingsForm.value };
    originalTenantSettings.value = { ...tenantSettingsForm.value };
    isEditingTenantSettings.value = false;
    
    store.dispatch('notify', {
      message: t('setupWizard.tenantSettingsSaved'),
      type: 'success'
    });
    
    // Close modal after successful save
    closeTenantSettingsModal();
    
    // Refresh tenant details if needed
    if (selectedTenantDetails.value) {
      viewNozzleTenantSetup(selectedTenantDetails.value.id);
    }
  } catch (err) {
    console.error('Error saving tenant settings:', err);
    const errorMessage = err.response?.data?.error?.message || 
                        err.response?.data?.message || 
                        t('setupWizard.tenantSettingsSaveError');
    store.dispatch('notify', {
      message: errorMessage,
      type: 'error'
    });
  } finally {
    isSavingTenantSettings.value = false;
  }
};

// Computed property to format JSON configuration
const formatJsonConfiguration = computed(() => {
  if (!selectedTenantDetails.value) return '';
  
  const urlWithSlash = apiBaseUrl.value.endsWith('/') ? apiBaseUrl.value : apiBaseUrl.value + '/';

  const config = {
    "Url": urlWithSlash,
    "TenantId": selectedTenantDetails.value.id || "",
    "Username": selectedTenantDetails.value.users?.[0]?.userName || "",
    "Password": selectedTenantDetails.value.users?.[0]?.plainPassword || "",
    "Environment": "Production",
    "IsVesProc": true
  };
  
  return JSON.stringify(config, null, 2);
});

// Computed property to format JSON with syntax highlighting
const formatJsonConfigurationWithHighlight = computed(() => {
  if (!selectedTenantDetails.value) return '';
  
  const urlWithSlash = apiBaseUrl.value.endsWith('/') ? apiBaseUrl.value : apiBaseUrl.value + '/';

  const config = {
    "Url": urlWithSlash,
    "TenantId": selectedTenantDetails.value.id || "",
    "Username": selectedTenantDetails.value.users?.[0]?.userName || "",
    "Password": selectedTenantDetails.value.users?.[0]?.plainPassword || "",
    "Environment": "Production",
    "IsVesProc": true
  };
  
  const jsonString = JSON.stringify(config, null, 2);
  
  // Apply syntax highlighting
  return jsonString
    .replace(/(".*?")\s*:/g, '<span class="json-key">$1</span>:')
    .replace(/:\s*(".*?")/g, ': <span class="json-string">$1</span>')
    .replace(/:\s*(true|false)/g, ': <span class="json-boolean">$1</span>')
    .replace(/:\s*(\d+)/g, ': <span class="json-number">$1</span>')
    .replace(/:\s*(null)/g, ': <span class="json-null">$1</span>')
    .replace(/([{}])/g, '<span class="json-brace">$1</span>')
    .replace(/([[\]])/g, '<span class="json-bracket">$1</span>')
    .replace(/(,)$/gm, '<span class="json-comma">$1</span>');
});

// Method to copy JSON configuration to clipboard
const copyJsonConfiguration = async () => {
  try {
    await navigator.clipboard.writeText(formatJsonConfiguration.value);
    store.dispatch('notify', {
      message: t('setupWizard.jsonCopied'),
      type: 'success'
    });
  } catch (err) {
    console.error('Copy to clipboard failed:', err);
    store.dispatch('notify', {
      message: t('setupWizard.jsonCopyError'),
      type: 'error'
    });
  }
};

// Method to test login with tenant ID and validate credentials
const testLogin = async () => {
  if (!selectedTenantDetails.value?.id) {
    store.dispatch('notify', {
      message: t('setupWizard.noTenantIdError'),
      type: 'error'
    });
    return;
  }

  isTestingLogin.value = true;
  error.value = null;

  try {
    // Test tenant login
    const tenantLoginData = {
      tenantId: selectedTenantDetails.value.id
    };

    // Test credential validation if supplier credentials are available
    let credentialValidationPromise = null;
    if (selectedTenantDetails.value.users?.[0]?.userName && selectedTenantDetails.value.users?.[0]?.plainPassword) {
      const credentialsData = {
        username: selectedTenantDetails.value.users[0].userName,
        password: selectedTenantDetails.value.users[0].plainPassword
      };
      credentialValidationPromise = superAdminSetupService.validateCredentials(credentialsData);
    }

    // Execute both tests
    const promises = [superAdminSetupService.testLogin(tenantLoginData)];
    if (credentialValidationPromise) {
      promises.push(credentialValidationPromise);
    }

    const results = await Promise.allSettled(promises);
    
    const tenantResult = results[0];
    const credentialResult = results[1];

    // Check results - tenant success means both HTTP success AND isSuccess: true
    const tenantSuccess = tenantResult.status === 'fulfilled' && tenantResult.value?.isSuccess === true;
    const credentialSuccess = credentialResult ? (credentialResult.status === 'fulfilled' && credentialResult.value?.isValid === true) : null;

    if (tenantSuccess && (credentialSuccess === null || credentialSuccess)) {
      store.dispatch('notify', {
        message: credentialSuccess === null 
          ? t('setupWizard.tenantLoginSuccess')
          : t('setupWizard.bothTestsSuccess'),
        type: 'success'
      });
    } else if (tenantSuccess && credentialSuccess === false) {
      store.dispatch('notify', {
        message: t('setupWizard.tenantSuccessCredentialFailed'),
        type: 'warning'
      });
    } else if (!tenantSuccess && credentialSuccess) {
      store.dispatch('notify', {
        message: t('setupWizard.credentialSuccessTenantFailed'),
        type: 'warning'
      });
    } else {
      // Both failed or tenant failed
      let errorMessage = t('setupWizard.testLoginError');
      
      if (tenantResult.status === 'fulfilled' && tenantResult.value) {
        // HTTP success but API returned failure
        errorMessage = tenantResult.value?.message || t('setupWizard.testLoginError');
      } else if (tenantResult.status === 'rejected') {
        // HTTP error
        errorMessage = tenantResult.reason?.response?.data?.error?.message || 
                      tenantResult.reason?.response?.data?.message || 
                      t('setupWizard.testLoginError');
      }
      
      error.value = errorMessage;
      store.dispatch('notify', {
        message: errorMessage,
        type: 'error'
      });
    }
  } catch (err) {
    console.error('Test login error:', err);
    const errorMessage = err.response?.data?.error?.message || 
                        err.response?.data?.message || 
                        t('setupWizard.testLoginError');
    
    error.value = errorMessage;
    store.dispatch('notify', {
      message: errorMessage,
      type: 'error'
    });
  } finally {
    isTestingLogin.value = false;
  }
};
</script>

<style scoped>
.setup-wizard-container {
  padding: 1rem 0;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0 !important;
  border: none;
}

.table th {
  font-weight: 600;
  color: #495057;
  border-top: none;
}

.btn {
  border-radius: 8px;
  font-weight: 500;
}

.form-control {
  border-radius: 8px;
  border: 1px solid #e1e5e9;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.badge {
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
}

.alert {
  border-radius: 8px;
  border: none;
}

/* JSON Formatting Styles */
.json-container {
  background-color: #1e1e1e !important;
  border: 1px solid #333;
  font-family: 'Courier New', monospace;
  overflow-x: auto;
}

.json-content {
  color: #d4d4d4;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
}

.json-key {
  color: #9cdcfe;
  font-weight: 500;
}

.json-string {
  color: #ce9178;
}

.json-number {
  color: #b5cea8;
}

.json-boolean {
  color: #569cd6;
  font-weight: bold;
}

.json-null {
  color: #569cd6;
  font-style: italic;
}

.json-brace,
.json-bracket {
  color: #ffd700;
  font-weight: bold;
}

.json-comma {
  color: #d4d4d4;
}
</style>
